import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { PLANTS, plateImg, pad2, cleanTribe } from '@/lib/databank';

const WANTED = ['Tagetes', 'Tridax', 'Zinnia', 'Cosmos', 'Helianthus', 'Calendula', 'Sonchus'];

const pickFeatured = () => {
    const picks = [];
    WANTED.forEach((w) => {
        const p = PLANTS.find((pl) => pl.botanical_name.startsWith(w));
        if (p && !picks.includes(p)) picks.push(p);
    });
    PLANTS.forEach((p) => {
        if (picks.length < 3 && !picks.includes(p)) picks.push(p);
    });
    return picks.slice(0, 3);
};

const SpotlightCard = ({ plant, index }) => {
    const reduce = useReducedMotion();
    return (
        <motion.div
            initial={reduce ? false : { opacity: 0, y: 56 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.9, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
            <Link to={`/plant/${plant.plant_no}`} data-testid={`featured-card-${plant.plant_no}`} className="group block">
                {/* clipped arch frame with spotlight */}
                <div className="relative overflow-hidden rounded-t-[999px] border border-line bg-[#FDFCF6] px-8 pt-10">
                    <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 55% at 50% 30%, rgba(255,255,255,0.95), rgba(229,225,213,0.4) 75%)' }} />
                    <img
                        src={plateImg(plant)}
                        alt={plant.botanical_name}
                        loading="lazy"
                        className="plate-img relative mx-auto w-full max-w-[280px] transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                </div>
                <div className="border border-t-0 border-line p-6">
                    <div className="flex items-baseline justify-between gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Plate {pad2(plant.plant_no)}</span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mute">{cleanTribe(plant.tribe)}</span>
                    </div>
                    <p className="mt-3 font-serif text-2xl italic leading-tight text-ink">{plant.botanical_name}</p>
                    <p className="mt-1 truncate text-sm text-mute">{plant.common_name.replace(/,$/, '')}</p>
                    <span className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
                        <span className="u-line">View record</span>
                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                </div>
            </Link>
        </motion.div>
    );
};

export const Featured = () => {
    const featured = pickFeatured();
    return (
        <section data-testid="featured-section" className="border-t border-line bg-sand/40">
            <div className="mx-auto max-w-[1500px] px-5 py-24 md:px-10 md:py-36">
                <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-terra">Spotlight</p>
                        <h2 className="mt-4 font-serif text-4xl italic text-ink md:text-6xl">Featured plates</h2>
                    </div>
                    <Link to="/gallery" data-testid="featured-open-index-cta" className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ink">
                        <span className="u-line">Open the full index — 72 records</span>
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </div>
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {featured.map((p, i) => (
                        <SpotlightCard key={p.plant_no} plant={p} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};
